import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, Subscription } from 'rxjs';
import { filter, finalize, switchMap } from 'rxjs/operators';
import moment from 'moment';
import { routes } from '@app/consts';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { NewEmployeeFormDialogComponent } from '@app/modules/talent-track/talent-track-profiles/components/new-employee-form-dialog/new-employee-form-dialog.component';
import { NewOrganizationFormDialogComponent } from '@app/modules/organization-structure/components/new-organization-form-dialog/new-organization-form-dialog.component';
import { PositionFormDialogComponent } from '@app/modules/org-chart/components/position-editor/position-form-dialog.component';
import { OrgChartService } from '@app/modules/org-chart/services/org-chart.service';
import { EmployeeDetails } from '@app/modules/org-chart/models/org-chart.model';
import { ChartDropEvent, ChartNode, ChartOrientation, ChartSearchItem, ChartType, PositionFilterDefinition } from './models/chart-node.model';
import { compareNames, OrgChartV2DataService } from './services/org-chart-v2-data.service';
import { ExportPage, OrgChartExportService, PaperSize } from './services/org-chart-export.service';
import { ChartSection, planSections } from './services/org-chart-pagination';
import { OrgChartDragService } from './services/org-chart-drag.service';
import { HierarchyChartComponent } from './components/hierarchy-chart/hierarchy-chart.component';
import { ChartNodeActionEvent } from './components/org-chart-node-card/org-chart-node-card.component';
import { OrgChartNodeDetailsData, OrgChartNodeDetailsDialogComponent } from './components/org-chart-node-details-dialog/org-chart-node-details-dialog.component';

type ExportFormat = 'pdf' | 'png' | 'excel';

export type ChartLayout = 'compact' | 'top' | 'left';

// Smallest scale a section of a paged export is shrunk to; cards' 15px names print at ~9pt.
const MIN_EXPORT_SCALE = 0.6;

@Component({
  selector: 'app-org-chart-v2',
  templateUrl: './org-chart-v2.component.html',
  styleUrls: ['./org-chart-v2.component.scss'],
  providers: [OrgChartDragService],
})
export class OrgChartV2Component implements OnInit, OnDestroy {
  @ViewChild(HierarchyChartComponent, { static: true }) chart: HierarchyChartComponent;

  chartType: ChartType = 'positions';
  editable = false;
  layout: ChartLayout = 'compact';

  roots: ChartNode[] = [];
  // Set when showing a single branch (search result or "Show only this branch")
  focusRoot: ChartNode | null = null;
  highlightedUid: string | null = null;

  isLoading = false;
  isExporting = false;
  // Show every card's employee list on screen, and whether exports include employees
  showAllEmployees = false;
  exportEmployees = false;
  exportProgress: string | null = null;

  // Paged exports: what is rendered in place of the chart, and where each branch continues
  private renderRoots: ChartNode[] | null = null;
  private continuations: Map<string, number> | null = null;
  private continuationUnit = 'page';
  private currentSectionUid: string | null = null;

  searchControl = new FormControl<string | ChartSearchItem>('');
  searchResults: ChartSearchItem[] = [];
  private searchIndex: ChartSearchItem[] = [];

  showFilters = false;
  filterDefinitions: PositionFilterDefinition[] = [];
  filterValues: Record<string, string[]> = {};
  visibleUids: Set<string> | null = null;

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private changeDetector: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private orgChartService: OrgChartService,
    private dataService: OrgChartV2DataService,
    private exportService: OrgChartExportService,
    private drag: OrgChartDragService,
  ) {
  }

  get fields() {
    return this.dataService.fields;
  }

  get title(): string {
    return this.chartType === 'organizations' ? 'Organization Chart' : 'Position Chart';
  }

  get displayRoots(): ChartNode[] {
    return this.renderRoots ?? (this.focusRoot ? [this.focusRoot] : this.roots);
  }

  get orientation(): ChartOrientation {
    return this.layout === 'left' ? 'left' : 'top';
  }

  get activeFilterCount(): number {
    return Object.values(this.filterValues).filter(values => values?.length).length;
  }

  ngOnInit() {
    this.subscriptions.add(this.route.paramMap.subscribe(params => {
      this.chartType = params.get('type') === 'organizations' ? 'organizations' : 'positions';
      this.editable = params.get('isEditable') === 'true';
      this.drag.enabled = this.editable;
      this.load();
    }));

    this.drag.isSameOrDescendant = (ancestor, node) => this.dataService.isSameOrDescendant(ancestor, node);
    this.subscriptions.add(this.drag.dropped.subscribe(event => this.handleDrop(event)));

    this.subscriptions.add(this.searchControl.valueChanges.subscribe(value => {
      this.searchResults = typeof value === 'string' ? this.dataService.search(this.searchIndex, value) : [];
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  load() {
    this.isLoading = true;
    this.roots = [];
    this.focusRoot = null;
    this.highlightedUid = null;
    this.filterValues = {};
    this.visibleUids = null;
    this.showFilters = false;

    const request: Observable<ChartNode[]> = this.chartType === 'organizations'
      ? this.dataService.loadOrganizations()
      : this.dataService.loadPositions();

    request.pipe(finalize(() => this.isLoading = false)).subscribe(roots => {
      this.roots = roots;
      this.refreshIndexes();
      setTimeout(() => this.chart?.fit());
    });
  }

  private refreshIndexes() {
    this.searchIndex = this.dataService.buildSearchIndex(this.roots);
    if (this.chartType === 'positions') {
      this.filterDefinitions = this.dataService.filterDefinitions(this.roots);
    }
    if (this.visibleUids) {
      this.applyFilters();
    }
  }

  // Expand / collapse / layout

  toggleNode(node: ChartNode) {
    node.expanded = !node.expanded;
  }

  expandAll() {
    this.dataService.setExpanded(this.displayRoots, true);
  }

  collapseAll() {
    this.dataService.setExpanded(this.displayRoots, false);
    setTimeout(() => this.chart?.fit());
  }

  toggleAllEmployees() {
    this.showAllEmployees = !this.showAllEmployees;
    this.exportEmployees = this.showAllEmployees;
    this.setEmployeesExpanded(this.roots, this.showAllEmployees);
  }

  private setEmployeesExpanded(roots: ChartNode[], expanded: boolean) {
    this.dataService.walk(roots, node => { node.employeesExpanded = expanded; });
  }

  setLayout(layout: ChartLayout) {
    this.layout = layout;
    setTimeout(() => this.chart?.fit());
  }

  // Search & focus

  displaySearchItem(item: ChartSearchItem | string): string {
    return typeof item === 'string' ? item : item?.label ?? '';
  }

  onSearchSelected(event: MatAutocompleteSelectedEvent) {
    const item: ChartSearchItem = event.option.value;
    // Employees live inside a card, so focus on the card that holds them
    const path = this.dataService.findPath(this.roots, item.node.uid);
    const branch = item.node.type === 'employee' ? path[path.length - 2] : item.node;
    if (!branch) {
      return;
    }
    if (item.node.type === 'employee') {
      branch.employeesExpanded = true;
    }
    this.focusOn(branch, item.node.uid);
    this.searchControl.setValue('', { emitEvent: false });
    this.searchResults = [];
  }

  focusOn(branch: ChartNode, highlightUid: string = branch.uid) {
    this.focusRoot = branch;
    this.dataService.setExpanded([branch], true);
    this.highlightedUid = highlightUid;
    setTimeout(() => {
      this.chart?.fit();
      this.chart?.scrollToNode(highlightUid);
    });
  }

  showAll() {
    this.focusRoot = null;
    this.highlightedUid = null;
    setTimeout(() => this.chart?.fit());
  }

  // Filters

  applyFilters() {
    this.visibleUids = this.dataService.applyFilters(this.roots, this.filterValues);
  }

  clearFilters() {
    this.filterValues = {};
    this.applyFilters();
  }

  // Node actions

  onNodeAction({ action, node }: ChartNodeActionEvent) {
    switch (action) {
      case 'details':
        this.showDetails(node);
        break;
      case 'focus':
        this.focusOn(node);
        break;
      case 'addPosition':
        this.openNewPositionDialog(node);
        break;
      case 'addEmployee':
        this.openNewEmployeeDialog(node);
        break;
      case 'addOrganization':
        this.openNewOrganizationDialog(node);
        break;
      case 'edit':
        this.edit(node);
        break;
      case 'delete':
        this.delete(node);
        break;
    }
  }

  private showDetails(node: ChartNode) {
    if (node.type !== 'employee') {
      this.openDetailsDialog({ title: node.name, subtitle: node.subtitle, fields: node.data });
      return;
    }

    this.orgChartService.getEmployeeDetails(node.id).subscribe((details: EmployeeDetails) => {
      const f = this.fields.EMPLOYEE;
      this.openDetailsDialog({
        title: node.name,
        subtitle: details.positions?.length ? details.positions.map(p => p.name).join(', ') : node.subtitle,
        photo: node.photo,
        fields: {
          [f.firstName]: details.firstname,
          [f.lastName]: details.lastname,
          [f.title]: details.title?.text,
          [f.preferred]: details.preferred,
          [f.maidenName]: details.maidenName,
          [f.middleName]: details.middleName,
          [f.gender]: details.gender?.text,
          [f.cultureAffil]: details.culturalAffiliation?.text,
          [f.religiousAffil]: details.religiousAffiliation?.text,
          [f.linkedin]: details.linkedInUrl,
          [f.twitter]: details.twitterHandle,
          [f.facebook]: details.facebookUrl,
          [f.status]: typeof details.status === 'string' ? details.status : (details.status as any)?.name,
          [f.birthday]: this.dataService.formatDate(details.birthday),
          [f.maritalStatus]: details.maritalStatus?.text,
          [f.subStatus]: details.subStatus,
          [f.language]: details.preferredLanguage,
          [f.streetAddress]: details.streetAddress,
          [f.city]: details.city,
          [f.province]: details.province,
          [f.zip]: details.zip,
          [f.country]: details.country?.name,
          [f.email]: details.email,
          [f.workPhone]: details.workPhone,
          [f.homePhone]: details.homePhone,
          [f.cellPhone]: details.cellPhone,
          [f.culture]: details.culture?.text,
        },
      });
    });
  }

  private openDetailsDialog(data: OrgChartNodeDetailsData) {
    this.dialog.open(OrgChartNodeDetailsDialogComponent, { data, minWidth: 420, maxWidth: '90vw', autoFocus: false });
  }

  private edit(node: ChartNode) {
    switch (node.type) {
      case 'position':
        this.router.navigate([`${routes.SITE_SETTINGS}${routes.POSITIONS}${routes.EDITOR.substring(1)}`, node.id]);
        break;
      case 'organization':
        this.router.navigate([`${routes.SITE_SETTINGS}${routes.ORGANIZATION}${routes.EDITOR}`, node.id]);
        break;
      case 'employee':
        this.router.navigate([`${routes.TALENT_TRACK}${routes.EDIT}`, node.id]);
        break;
    }
  }

  private delete(node: ChartNode) {
    switch (node.type) {
      case 'position':
        if (node.children.length || node.employees.length) {
          this.snackbarService.openSnackBar('Cannot delete position with employees or reporting positions', 'clear', 'warn');
          return;
        }
        this.confirm('Are you sure you want to delete this position ?')
          .pipe(switchMap(() => this.orgChartService.deletePosition(node.id)))
          .subscribe(() => {
            this.removeFromChart(node);
            this.snackbarService.openSnackBar('Position deleted successfully', 'clear', 'success');
          });
        break;
      case 'organization':
        if (node.children.length) {
          this.snackbarService.openSnackBar('Cannot delete organization with underlying organizations', 'clear', 'warn');
          return;
        }
        this.confirm('Are you sure you want to delete this organization ?')
          .pipe(switchMap(() => this.orgChartService.deleteOrganization(node.id)))
          .subscribe(() => {
            this.removeFromChart(node);
            this.snackbarService.openSnackBar('Organization deleted successfully', 'clear', 'success');
          });
        break;
      case 'employee':
        this.confirm('Are you sure you want to delete this employee position?')
          .pipe(switchMap(() => this.orgChartService.deleteEmployeePosition(node.id)))
          .subscribe(() => {
            this.removeFromChart(node);
            this.snackbarService.openSnackBar('Employee position deleted successfully', 'clear', 'success');
          });
        break;
    }
  }

  private removeFromChart(node: ChartNode) {
    const parent = node.parentId ? this.dataService.findByUid(this.roots, node.parentId) : null;
    this.roots = this.dataService.detach(this.roots, node);
    if (this.focusRoot === node) {
      this.focusRoot = null;
    }
    if (parent?.type === 'position') {
      this.updateVacancy(parent);
    }
    this.refreshIndexes();
  }

  // Emits once if the user confirms, otherwise completes without emitting.
  private confirm(text: string): Observable<boolean> {
    const config = new MatDialogConfig();
    config.disableClose = true;
    config.autoFocus = true;
    config.data = { text };
    return this.dialog.open(ConfirmDialogComponent, config).afterClosed().pipe(filter(result => result === true));
  }

  // Adding

  private openNewPositionDialog(parent: ChartNode) {
    const config = new MatDialogConfig();
    config.autoFocus = true;
    config.minWidth = 500;
    config.data = { position: null, parentId: parent.id };

    this.dialog.open(PositionFormDialogComponent, config).afterClosed().subscribe(data => {
      if (data?.result && data.formData) {
        this.addChild(parent, this.positionNodeFromForm(data.result.positionId, parent.id, data.formData));
      }
    });
  }

  private positionNodeFromForm(id: string, parentId: string, form: any): ChartNode {
    const f = this.fields.POSITION;
    const name = form.name?.[0]?.text ?? '';
    const node = this.dataService.createNode('position', id, parentId, name, {
      [f.positionName]: name,
      [f.jobTitle]: form.functionalJobTitle?.text ?? null,
      [f.marketPosition]: form.marketPosition?.text ?? null,
      [f.marketView]: form.marketView?.text ?? null,
      [f.unionCode]: form.unionCode?.text ?? null,
      [f.shiftCode]: form.shiftCode?.text ?? null,
      [f.numberSlots]: form.numberOfSlots ?? null,
      [f.enforceSlotLimit]: form.enforceSlotLimit ?? null,
      [f.department]: form.department?.text ?? null,
      [f.division]: form.division?.text ?? null,
      [f.empCategory]: form.employeeCategory?.text ?? null,
      [f.empGroup]: form.employmentGroup?.text ?? null,
      [f.empType]: form.employmentType?.text ?? null,
      [f.jobBand]: form.jobBand?.text ?? null,
      [f.jobFamily]: form.jobFamily?.text ?? null,
      [f.jobGroup]: form.jobGroup?.text ?? null,
      [f.jobType]: form.jobType?.text ?? null,
      [f.projectTeam]: form.projectTeam?.text ?? null,
      [f.region]: form.region?.text ?? null,
      [f.workLocation]: form.workLocation?.name ?? null,
      [f.subDivision]: form.subDivision?.text ?? null,
      [f.workRotation]: form.workRotation?.name ?? null,
      [f.workRotationStart]: form.workRotationStartDate ? moment(form.workRotationStartDate).format('DD-MMM-YYYY') : null,
      [f.status]: 'Active',
    });
    node.subtitle = form.department?.text ?? undefined;
    node.slots = form.numberOfSlots || undefined;
    this.updateVacancy(node);
    return node;
  }

  private openNewEmployeeDialog(position: ChartNode) {
    const config = new MatDialogConfig();
    config.disableClose = true;
    config.autoFocus = true;
    config.minWidth = 500;

    this.dialog.open(NewEmployeeFormDialogComponent, config).afterClosed().pipe(
      filter(data => !!data?.result),
      switchMap(data => {
        const body = { positionId: position.id, employeeId: data.result.employeeId, startDate: new Date(), endDate: null };
        return this.orgChartService.postEmployeeReportToPosition(body).pipe(
          switchMap(() => [data])
        );
      })
    ).subscribe(data => {
      const employee = this.dataService.employeeToNode(position, data.result.employeeId, data.formData.firstname, data.formData.lastname);
      this.addEmployee(position, employee);
      this.snackbarService.openSnackBar('Employee created successfully', 'clear', 'success');
    });
  }

  private openNewOrganizationDialog(parent: ChartNode) {
    const config = new MatDialogConfig();
    config.autoFocus = true;
    config.minWidth = 500;
    config.data = { position: null, parentId: parent.id };

    this.dialog.open(NewOrganizationFormDialogComponent, config).afterClosed().subscribe(data => {
      if (data?.result && data.formData) {
        const f = this.fields.ORGANIZATION;
        const name = data.formData.name?.[0]?.text ?? '';
        const parentOrganization = data.formData.parentOrganization;
        const parentId = (typeof parentOrganization === 'string' ? parentOrganization : parentOrganization?.id) ?? parent.id;
        const node = this.dataService.createNode('organization', data.result.organizationId, parentId, name, {
          [f.name]: name,
          [f.startDate]: this.dataService.formatDate(data.formData.startDate),
          [f.endDate]: this.dataService.formatDate(data.formData.endDate),
        });
        const target = this.dataService.findByUid(this.roots, parentId) ?? parent;
        this.addChild(target, node);
        this.snackbarService.openSnackBar('Organization created successfully', 'clear', 'success');
      }
    });
  }

  private addChild(parent: ChartNode, node: ChartNode) {
    node.parentId = parent.id;
    parent.children = [...parent.children, node];
    this.dataService.sortTree(parent.children);
    parent.expanded = true;
    this.refreshIndexes();
    this.highlightedUid = node.uid;
    this.chart?.scrollToNode(node.uid);
  }

  private addEmployee(position: ChartNode, employee: ChartNode) {
    position.employees = [...position.employees, employee].sort(compareNames);
    position.employeesExpanded = true;
    this.updateVacancy(position);
    this.refreshIndexes();
  }

  private updateVacancy(position: ChartNode) {
    if (position.type !== 'position') {
      return;
    }
    position.isVacant = position.slots ? position.slots > position.employees.length : position.employees.length === 0;
    position.data[this.fields.POSITION.isVacant] = position.isVacant ? 'Vacant' : 'Occupied';
  }

  // Drag & drop

  private handleDrop({ dragged, target }: ChartDropEvent) {
    if (dragged.type === 'employee') {
      const body = { positionId: target.id, employeeId: dragged.id, startDate: new Date(), endDate: null };
      this.orgChartService.postEmployeeReportToPosition(body).subscribe(() => {
        const employee = this.dataService.employeeToNode(target, dragged.id, dragged.data[this.fields.EMPLOYEE.firstName], dragged.data[this.fields.EMPLOYEE.lastName], dragged.photo, dragged.subtitle);
        this.addEmployee(target, employee);
        this.snackbarService.openSnackBar(`${dragged.name} assigned to ${target.name}`, 'clear', 'success');
      });
      return;
    }

    const body = { id: dragged.id, asOf: new Date(), changeReason: '', changeReasonComments: '', parentPosition: target.id };
    const request = dragged.type === 'position'
      ? this.orgChartService.postPositionReportToPosition(dragged.id, body)
      : this.orgChartService.postOrganizationReportToOrganization(dragged.id, body);

    this.confirm(`Are you sure you want to re-parent this ${dragged.type}?`)
      .pipe(switchMap(() => request))
      .subscribe(() => {
        this.roots = this.dataService.detach(this.roots, dragged);
        this.addChild(target, dragged);
      });
  }

  // Export

  async export(format: ExportFormat, expandAll = false) {
    if (this.isExporting) {
      return;
    }
    if (format === 'excel') {
      await this.exportService.exportExcel(this.displayRoots, this.title, this.exportEmployees);
      return;
    }

    await this.runExport(expandAll, async () => {
      await this.nextRender();
      const tree = this.chart.treeElement;
      if (format === 'pdf') {
        await this.exportService.exportPdf(tree, this.title);
      } else {
        await this.exportService.exportPng(tree, this.title);
      }
    });
  }

  // Page 1 is the whole chart; the following pages split it into sections that stay readable.
  async exportPaged(format: 'pdf' | 'pptx', paper: PaperSize = 'A4') {
    if (this.isExporting) {
      return;
    }

    await this.runExport(true, async () => {
      const roots = this.displayRoots;
      this.exportProgress = 'Planning pages…';
      await this.nextRender();

      const sections = planSections(roots, {
        box: format === 'pdf' ? this.exportService.pdfChartBox(paper) : this.exportService.slideChartBox(),
        minScale: MIN_EXPORT_SCALE,
        childrenOf: node => this.chart.visible(node.children),
        measure: root => {
          this.renderRoots = [root];
          this.changeDetector.detectChanges();
          const tree = this.chart.treeElement;
          return { width: tree.offsetWidth, height: tree.offsetHeight };
        },
      });

      const pages = this.buildExportPages(roots, sections, format === 'pdf' ? 'page' : 'slide');
      const onProgress = (page: number) => this.exportProgress = `Exporting ${format === 'pdf' ? 'page' : 'slide'} ${page} of ${pages.length}…`;
      if (format === 'pdf') {
        await this.exportService.exportPagedPdf(this.title, paper, pages, onProgress);
      } else {
        await this.exportService.exportPowerPoint(this.title, pages, onProgress);
      }
    });
  }

  private buildExportPages(roots: ChartNode[], sections: ChartSection[], unit: string): ExportPage[] {
    // Page 1 is the overview, so sections start on page 2. Parts 2+ of a split team aren't linked to.
    this.continuations = new Map();
    this.continuationUnit = unit;
    sections.forEach((section, index) => {
      if (!this.continuations.has(section.source.uid)) {
        this.continuations.set(section.source.uid, index + 2);
      }
    });

    const render = (renderRoots: ChartNode[], sectionUid: string | null) => async () => {
      this.renderRoots = renderRoots;
      this.currentSectionUid = sectionUid;
      await this.nextRender();
      return this.chart.treeElement;
    };

    return [
      { subtitle: 'Overview', render: render(roots, null) },
      ...sections.map(section => ({
        subtitle: [...section.path, section.source].map(node => node.name).join(' > ')
          + (section.parts > 1 ? ` (part ${section.part} of ${section.parts})` : ''),
        render: render([section.root], section.source.uid),
      })),
    ];
  }

  continuedOn(node: ChartNode): string | null {
    const page = node.uid !== this.currentSectionUid ? this.continuations?.get(node.uid) : undefined;
    return page ? `${this.continuationUnit} ${page}` : null;
  }

  private async runExport(expandAll: boolean, work: () => Promise<void>) {
    this.isExporting = true;
    const restore = this.prepareForCapture(expandAll);
    try {
      await work();
    } catch (error) {
      console.error(error);
      this.snackbarService.openSnackBar('Export failed', 'clear', 'warn');
    } finally {
      restore();
      this.isExporting = false;
      this.exportProgress = null;
    }
  }

  // Puts the chart in a printable state and returns a function that restores the previous one.
  private prepareForCapture(expandAll: boolean): () => void {
    const highlighted = this.highlightedUid;
    const expanded = new Map<ChartNode, [boolean, boolean]>();
    this.dataService.walk(this.roots, node => { expanded.set(node, [node.expanded, node.employeesExpanded]); });

    this.highlightedUid = null;
    if (expandAll) {
      this.dataService.setExpanded(this.displayRoots, true);
    }
    this.setEmployeesExpanded(this.roots, this.exportEmployees);
    this.chart.canvasElement.classList.add('exporting');

    return () => {
      this.chart.canvasElement.classList.remove('exporting');
      expanded.forEach(([nodeExpanded, employeesExpanded], node) => {
        node.expanded = nodeExpanded;
        node.employeesExpanded = employeesExpanded;
      });
      this.renderRoots = null;
      this.continuations = null;
      this.currentSectionUid = null;
      this.highlightedUid = highlighted;
    };
  }

  private nextRender(): Promise<void> {
    this.changeDetector.detectChanges();
    // Give images a moment to start loading after the DOM changes
    return new Promise(resolve => setTimeout(resolve, 100));
  }
}
